// An agent paused on its provider's plan usage limit: the words the band, the header and the held
// line say, and which other AIs signed in on the agent's machine can answer instead.
import { PROVIDER_LABELS } from "$lib/agent-display.js";
import type { ProviderInfo } from "./types.js";

export interface UsageLimit {
  provider: string;
  window?: string; // the provider's rateLimitType
  resetsAt?: number; // epoch ms
  plan?: string; // "Max"
  until?: number; // epoch ms: when the daemon next sends what it holds
  auth?: ProviderAuthFailure; // a login refusal, not a plan limit: held until the login is back
}

export type ProviderAuthFailure = "auth_missing" | "auth_expired" | "keychain_unavailable";

/** The owner's band for a login refusal: what happened on that computer, and the one button. */
export function authCopy(
  limit: UsageLimit & { auth: ProviderAuthFailure },
  machine: string,
): { title: string; action: string } {
  const label = providerLabel(limit.provider);
  if (limit.auth === "keychain_unavailable") {
    return {
      title: `Cyborg's background service on ${machine} lost access to its keychain.`,
      action: "Restart Cyborg's background service",
    };
  }
  const title =
    limit.auth === "auth_expired"
      ? `${label}'s sign-in on ${machine} expired.`
      : `${label} isn't signed in on ${machine}.`;
  return { title, action: `Sign in to ${label} again` };
}

/** When the pause ends: the provider's reset, or the daemon's later retry once that has passed. */
export function pauseEnd(limit: UsageLimit): number | undefined {
  return limit.resetsAt === undefined ? undefined : Math.max(limit.resetsAt, limit.until ?? 0);
}

const WINDOWS: Record<string, string> = {
  five_hour: "5-hour limit",
  seven_day: "weekly limit",
  seven_day_opus: "weekly Opus limit",
  seven_day_sonnet: "weekly Sonnet limit",
  session_limit: "session limit",
  weekly_limit: "weekly limit",
  opus_limit: "weekly Opus limit",
  sonnet_limit: "weekly Sonnet limit",
};

export function windowLabel(window: string | undefined): string {
  return (window && WINDOWS[window]) ?? "usage limit";
}

export function providerLabel(provider: string): string {
  return PROVIDER_LABELS[provider] ?? provider;
}

// Only the Claude harness can limit a teammate's turn (the daemon's assertContainmentEmitted
// refuses every other one), so an agent on any other AI answers its owner alone.
// ponytail: mirrors the server's one containable harness; a new one that can limit tools goes here too.
const ANSWERS_TEAMMATES: ReadonlySet<string> = new Set(["claude"]);

/** The owner's warning before moving an agent onto an AI teammates can't use; undefined when every one can. */
export function teammateNote(agentName: string, providers: readonly string[]): string | undefined {
  const off = [...new Set(providers.filter((p) => !ANSWERS_TEAMMATES.has(p)).map(providerLabel))];
  return off.length
    ? `Teammates can't message ${agentName} while it runs on ${off.join(" or ")}.`
    : undefined;
}

/** "Claude Max" / "Claude" */
export function planName(limit: UsageLimit): string {
  const label = providerLabel(limit.provider);
  return limit.plan ? `${label} ${limit.plan}` : label;
}

/** "today at 3:00 PM", "tomorrow at 9:00 AM", "Thursday at 9:00 AM", "Oct 30 at 9:00 AM". */
export function resetPhrase(at: number, now = Date.now()): string {
  const d = new Date(at);
  const time = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  const day = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const days = Math.round((day(d) - day(new Date(now))) / 86_400_000);
  if (days <= 0) return `today at ${time}`;
  if (days === 1) return `tomorrow at ${time}`;
  if (days < 7) return `${d.toLocaleDateString([], { weekday: "long" })} at ${time}`;
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })} at ${time}`;
}

export function heldLine(heldUntil: number | undefined, limit?: UsageLimit): string {
  if (limit?.auth === "keychain_unavailable") return "Held · sends once Cyborg restarts";
  if (limit?.auth) return "Held · sends once the sign-in is fixed";
  return heldUntil
    ? `Held · sends ${resetPhrase(heldUntil)}`
    : "Held · sends when the limit resets";
}

export interface ModelChoice {
  providerId: string;
  providerLabel: string;
  modelId: string;
  modelLabel: string;
}

const HIDDEN = new Set(["mock", "mock-slow"]); // dev-only providers, never offered to a person

// A harness that can sign in with the vendor's own login (Pi, OpenCode) hits the same wall on that
// vendor's models. ponytail: the model's vendor stands in for the auth type the wire lacks.
const SHARED_LOGIN = new Set(["pi", "opencode"]);
const VENDOR_MODELS: Record<string, RegExp> = {
  claude: /claude|opus|sonnet|haiku/i,
  codex: /gpt|codex|\bo\d/i,
};

/** One model per OTHER AI signed in on the agent's machine: its default, else its first. */
export function switchChoices(providers: readonly ProviderInfo[], limited: string): ModelChoice[] {
  const sameVendor = VENDOR_MODELS[limited];
  const out: ModelChoice[] = [];
  for (const p of providers) {
    if (!p.available || p.catalog || HIDDEN.has(p.id) || p.id === limited) continue;
    const m = p.models.find((x) => x.isDefault) ?? p.models[0];
    if (!m || (SHARED_LOGIN.has(p.id) && sameVendor?.test(`${m.id} ${m.label ?? ""}`))) continue;
    out.push({
      providerId: p.id,
      providerLabel: p.label,
      modelId: m.id,
      modelLabel: m.label ?? m.id,
    });
  }
  return out;
}

// The stored turn failure is a plan's usage window, not billing (server provider-error-detail.ts
// classOf): such a row draws nothing, the paused agent's band says it.
const LIMIT_WORDS = [
  "usage limit",
  "usagelimitexceeded",
  "hit your limit",
  "session limit",
  "weekly limit",
  "opus limit",
  "sonnet limit",
  "rate_limit: rejected",
];
const QUOTA_WORDS =
  /insufficient_quota|no credits remaining|exceeded your current quota|billing_hard_limit|credit balance is too low|insufficient (?:credits|balance)|extra usage|settings\/usage/i;

export function isUsageLimitFailure(text: string): boolean {
  const lower = text.toLowerCase();
  return LIMIT_WORDS.some((w) => lower.includes(w)) && !QUOTA_WORDS.test(text);
}
